import { useColorImages } from "../data/useColorImages";
import { useEffect, useRef, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import * as BABYLON from "@babylonjs/core";

import {
  Engine,
  Scene,
  Vector3,
  MeshBuilder,
  StandardMaterial,
  Color3,
  Color4,
  ArcRotateCamera,
  HemisphericLight,
  Texture,
  ActionManager
} from "@babylonjs/core";
import { Modal, Button } from "react-bootstrap";
import { getAllObjects } from "../data/getters";
import FloatingToolbar from "../modules/helpers/FloatingToolbar";
import useMillingDisplay from "./useMillingDisplay";

export default function ObjectViewer({
  object: objectProp,
  createdItems,
  usedColors: catalogColors,
  onItemUpdate,
  hiddenItems
}) {
  const visibleColors = useMemo(() => {
    const ids = new Set((createdItems || []).filter(item => !hiddenItems?.has(item.itemId ?? item.tmpId)).map(item => String(item.color?.colorId)));
    return (catalogColors || []).filter(color => ids.has(String(color.colorId)));
  }, [catalogColors, createdItems, hiddenItems]);
  const usedColors = useColorImages(visibleColors);
  const dispatch = useDispatch();
  const millingDisplay = useMillingDisplay();
  const canvasRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const [objectData, setObjectData] = useState(null);
  const colors = usedColors ?? [];
  const selectedMeshRef = useRef(null);
  const [groupingMode, setGroupingMode] = useState(false);
  const groupingModeRef = useRef(false);
  const initialItemTransformsRef = useRef(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const containerRef = useRef(null);
  const activeGroupMeshesRef = useRef([]);
  const [showInfoPanel, setShowInfoPanel] = useState(false);
  const infoPanelRef = useRef(null);

  const [measureMode, setMeasureMode] = useState(false); // későbbre
  const [isSnappingEnabled, setIsSnappingEnabled] = useState(true); // későbbre
  const [controlMode, setControlMode] = useState("object"); // camera | object
  const [transformMode, setTransformMode] = useState("move"); // move | rotate
  const transformModeRef = useRef(transformMode);
  const snappingEnabledRef = useRef(isSnappingEnabled);
  const measureModeRef = useRef(false);
  const firstPickRef = useRef(null);
  const measureVisualsRef = useRef({
    p1: null,
    p1dot: null,
    p1center: null,
    p2: null,
    p2dot: null,
    p2center: null,
    line: null,
    label: null
  });

  function collectAllMeshPositionUpdates(scene) {
    const byItem = new Map();
    scene.meshes.forEach((mesh) => {
      if (!mesh.name.startsWith("box_")) return;
      const { itemId, instIdx } = mesh.metadata || {};
      if (itemId == null || instIdx == null) return;
      if (!byItem.has(itemId)) byItem.set(itemId, []);
      byItem.get(itemId).push({ instIdx, mesh });
    });
    const updates = [];
    byItem.forEach((entries, itemId) => {
      const item = createdItems.find((it) => it.itemId === itemId);
      if (!item) return;
      const posList = parseBracketList(item.position, item.qty);
      const rotList = parseBracketList(item.rotation, item.qty);
      const maxDim = Math.max(
        ...createdItems.map((it) => parseSizeString(it.size)).flat(),
        1
      );
      const globalScale = 1 / maxDim;
      entries.forEach(({ instIdx, mesh }) => {
        const pivot = mesh.getPivotPoint();
        const px = pivot?.x ?? 0;
        const py = pivot?.y ?? 0;
        const pz = pivot?.z ?? 0;
        const cx = mesh.position.x / globalScale;
        const cy = mesh.position.y / globalScale;
        const cz = mesh.position.z / globalScale;
        posList[instIdx] = [
          +(cx + px / globalScale).toFixed(1),
          +(cy + py / globalScale).toFixed(1),
          +(cz + pz / globalScale).toFixed(1)
        ];
        rotList[instIdx] = [
          +(mesh.rotation.x / (Math.PI / 2)).toFixed(3),
          +(mesh.rotation.y / (Math.PI / 2)).toFixed(3),
          +(mesh.rotation.z / (Math.PI / 2)).toFixed(3)
        ];
      });
      updates.push({
        uid: item.uid,
        itemId,
        position: posList.map((p) => `[${p.join(",")}]`).join(","),
        rotation: rotList.map((r) => `[${r.join(",")}]`).join(",")
      });
    });
    return updates;
  }

  useEffect(() => {
    groupingModeRef.current = groupingMode;
  }, [groupingMode]);

  useEffect(() => {
    snappingEnabledRef.current = isSnappingEnabled;
  }, [isSnappingEnabled]);
  useEffect(() => {
    measureModeRef.current = measureMode;
  }, [measureMode]);

  const dragStateRef = useRef({
    active: false,
    button: null,
    startX: 0,
    startY: 0,
    lastX: 0,
    lastY: 0,
    startPos: null,
    startRot: null,
    moved: false,
    altKey: false // ⬅️ ÚJ
  });

  function getInteractionSensitivity() {
    const isTabletViewport =
      typeof window !== "undefined" &&
      window.matchMedia("(max-width: 1300px)").matches;

    return {
      move: isTabletViewport ? 0.45 : 1,
      rotate: isTabletViewport ? 0.5 : 1
    };
  }

  function createMeasureMaterial(scene) {
    const mat = new StandardMaterial("measureMat", scene);
    mat.diffuseColor = new Color3(0, 1, 0);
    mat.emissiveColor = new Color3(0, 1, 0);
    mat.specularColor = Color3.Black();
    mat.disableLighting = true;
    return mat;
  }

  function orientRingToNormal(mesh, normal) {
    if (!normal) return;
    const n = normal.normalize();
    const q = BABYLON.Quaternion.FromLookDirectionLH(n, Vector3.Up());
    const correction = BABYLON.Quaternion.RotationAxis(
      Vector3.Right(),
      Math.PI / 2
    );
    mesh.rotationQuaternion = q.multiply(correction);
  }

  function createMeasureInnerCircle(scene, position, normal, material) {
    const disk = MeshBuilder.CreateCylinder(
      "measure_inner",
      { diameter: 0.02, height: 0.0008, tessellation: 32 },
      scene
    );
    disk.position = position.clone();
    disk.material = material;
    disk.isPickable = false;
    orientRingToNormal(disk, normal);
    return disk;
  }

  function createMeasureCenterDot(scene, position, normal) {
    const mat = new StandardMaterial("measureCenterMat", scene);
    mat.diffuseColor = Color3.Black();
    mat.emissiveColor = Color3.Black();
    mat.disableLighting = true;

    const dot = MeshBuilder.CreateCylinder(
      "measure_center_dot",
      { diameter: 0.008, height: 0.0006, tessellation: 24 },
      scene
    );
    dot.position = position.clone();
    dot.material = mat;
    dot.isPickable = false;

    if (normal) {
      dot.position.addInPlace(normal.normalize().scale(0.0005));
      orientRingToNormal(dot, normal);
    }
    return dot;
  }

  function clearMeasurement(scene) {
    const v = measureVisualsRef.current;
    Object.values(v).forEach((m) => m && m.dispose());
    measureVisualsRef.current = {
      p1: null,
      p1dot: null,
      p1center: null,
      p2: null,
      p2dot: null,
      p2center: null,
      line: null,
      label: null
    };
  }

  function hasCollision(scene, movingMesh) {
    const bi = movingMesh.getBoundingInfo().boundingBox;
    for (const m of scene.meshes) {
      if (m === movingMesh) continue;
      if (!m.name.startsWith("box_")) continue;
      const obi = m.getBoundingInfo().boundingBox;
      const overlap =
        bi.maximumWorld.x >= obi.minimumWorld.x &&
        bi.minimumWorld.x <= obi.maximumWorld.x &&
        bi.maximumWorld.y >= obi.minimumWorld.y &&
        bi.minimumWorld.y <= obi.maximumWorld.y &&
        bi.maximumWorld.z >= obi.minimumWorld.z &&
        bi.minimumWorld.z <= obi.maximumWorld.z;
      if (overlap) return true;
    }
    return false;
  }
  const applyCollisionVisual = (mesh) => {
    if (!mesh?.material) return;
    mesh.material.emissiveColor = new Color3(0.6, 0.1, 0.1);
    mesh.edgesColor = new Color4(1, 0, 0, 1);
  };
  const clearCollisionVisual = (mesh) => {
    if (!mesh?.material) return;
    mesh.material.emissiveColor = Color3.Black();
    mesh.edgesColor = new Color4(0, 0, 0, 1);
  };

  const SNAP_DISTANCE_FACTOR = 1; // 10–25% jó tartomány

  const snapXZ = (scene, movingMesh) => {
    const bi = movingMesh.getBoundingInfo().boundingBox;

    const sizeX = bi.maximumWorld.x - bi.minimumWorld.x;
    const sizeZ = bi.maximumWorld.z - bi.minimumWorld.z;

    const SNAP = Math.min(sizeX, sizeZ) * SNAP_DISTANCE_FACTOR;
    const localMin = bi.minimumWorld.subtract(movingMesh.position);
    const localMax = bi.maximumWorld.subtract(movingMesh.position);

    let bestDx = null;
    let bestDz = null;

    for (const m of scene.meshes) {
      if (m === movingMesh) continue;
      if (!m.name.startsWith("box_")) continue;

      const obi = m.getBoundingInfo().boundingBox;

      // ───── X SNAP (Z overlap kell)
      const zOverlap =
        movingMesh.position.z + localMax.z >= obi.minimumWorld.z &&
        movingMesh.position.z + localMin.z <= obi.maximumWorld.z;

      if (zOverlap) {
        const dx1 = obi.maximumWorld.x - (movingMesh.position.x + localMin.x);
        const dx2 = obi.minimumWorld.x - (movingMesh.position.x + localMax.x);

        if (
          Math.abs(dx1) < SNAP &&
          (bestDx === null || Math.abs(dx1) < Math.abs(bestDx))
        ) {
          bestDx = dx1;
        }
        if (
          Math.abs(dx2) < SNAP &&
          (bestDx === null || Math.abs(dx2) < Math.abs(bestDx))
        ) {
          bestDx = dx2;
        }
      }

      // ───── Z SNAP (X overlap kell)
      const xOverlap =
        movingMesh.position.x + localMax.x >= obi.minimumWorld.x &&
        movingMesh.position.x + localMin.x <= obi.maximumWorld.x;

      if (xOverlap) {
        const dz1 = obi.maximumWorld.z - (movingMesh.position.z + localMin.z);
        const dz2 = obi.minimumWorld.z - (movingMesh.position.z + localMax.z);

        if (
          Math.abs(dz1) < SNAP &&
          (bestDz === null || Math.abs(dz1) < Math.abs(bestDz))
        ) {
          bestDz = dz1;
        }
        if (
          Math.abs(dz2) < SNAP &&
          (bestDz === null || Math.abs(dz2) < Math.abs(bestDz))
        ) {
          bestDz = dz2;
        }
      }
    }

    return {
      dx: bestDx ?? 0,
      dz: bestDz ?? 0
    };
  };
  const snapY = (scene, movingMesh) => {
    const bi = movingMesh.getBoundingInfo().boundingBox;

    const sizeX = bi.maximumWorld.x - bi.minimumWorld.x;
    const sizeY = bi.maximumWorld.y - bi.minimumWorld.y;
    const sizeZ = bi.maximumWorld.z - bi.minimumWorld.z;

    const isVertical = sizeY > sizeX * 2 && sizeY > sizeZ * 2;

    const SNAP = Math.min(sizeX, sizeZ) * 0.25;

    const localMin = bi.minimumWorld.subtract(movingMesh.position);
    const localMax = bi.maximumWorld.subtract(movingMesh.position);

    const movingBottom = movingMesh.position.y + localMin.y;
    const movingTop = movingMesh.position.y + localMax.y;

    let bestDy = null;

    for (const m of scene.meshes) {
      if (m === movingMesh) continue;
      if (!m.name.startsWith("box_")) continue;

      const obi = m.getBoundingInfo().boundingBox;

      // XZ overlap kötelező
      const xOverlap =
        movingMesh.position.x + localMax.x >= obi.minimumWorld.x &&
        movingMesh.position.x + localMin.x <= obi.maximumWorld.x;

      const zOverlap =
        movingMesh.position.z + localMax.z >= obi.minimumWorld.z &&
        movingMesh.position.z + localMin.z <= obi.maximumWorld.z;

      let overlapOK;

      if (isVertical) {
        // álló elemnél elég, ha az egyik irány fed
        overlapOK = xOverlap || zOverlap;
      } else {
        // fekvő elemnél teljes XZ fedés kell
        overlapOK = xOverlap && zOverlap;
      }
      const edgeX = movingMesh.position.x;
      const edgeZ = movingMesh.position.z;

      const otherMinX = obi.minimumWorld.x;
      const otherMaxX = obi.maximumWorld.x;
      const otherMinZ = obi.minimumWorld.z;
      const otherMaxZ = obi.maximumWorld.z;

      // él → lap távolság XZ síkban
      const dx =
        edgeX < otherMinX
          ? otherMinX - edgeX
          : edgeX > otherMaxX
            ? edgeX - otherMaxX
            : 0;

      const dz =
        edgeZ < otherMinZ
          ? otherMinZ - edgeZ
          : edgeZ > otherMaxZ
            ? edgeZ - otherMaxZ
            : 0;

      const edgeDistance = Math.hypot(dx, dz);
      if (isVertical) {
        if (edgeDistance > SNAP) continue;
      } else {
        if (!overlapOK) continue;
      }

      const otherBottom = obi.minimumWorld.y;
      const otherTop = obi.maximumWorld.y;

      // alulról stack
      if (movingBottom >= otherTop) {
        const dy = otherTop - movingBottom;
        if (
          Math.abs(dy) < SNAP &&
          (bestDy === null || Math.abs(dy) < Math.abs(bestDy))
        ) {
          bestDy = dy;
        }
      }

      // felülről
      if (movingTop <= otherBottom) {
        const dy = otherBottom - movingTop;
        if (
          Math.abs(dy) < SNAP &&
          (bestDy === null || Math.abs(dy) < Math.abs(bestDy))
        ) {
          bestDy = dy;
        }
      }
    }
    return {
      dy: bestDy ?? 0
    };
  };
  useEffect(() => {
    const onClickOutside = (e) => {
      if (
        showInfoPanel &&
        infoPanelRef.current &&
        !infoPanelRef.current.contains(e.target)
      ) {
        setShowInfoPanel(false);
      }
    };
    window.addEventListener("mousedown", onClickOutside);
    return () => window.removeEventListener("mousedown", onClickOutside);
  }, [showInfoPanel]);
  function DragHint({ axis }) {
    return (
      <span style={{ fontSize: "0.65rem", opacity: 0.8 }}>
        húzás {axis && `(${axis})`}
      </span>
    );
  }

  function MouseIcon({ button }) {
    const isLeft = button === "left";
    const isRight = button === "right";

    return (
      <div
        style={{
          width: 14,
          height: 20,
          border: "1px solid #444",
          borderRadius: "7px",
          position: "relative",
          background: "#f9f9f9"
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 1,
            left: 1,
            width: 5,
            height: 7,
            borderRadius: "3px",
            background: isLeft ? "#333" : "#ccc"
          }}
        />
        <div
          style={{
            position: "absolute",
            top: 1,
            right: 1,
            width: 5,
            height: 7,
            borderRadius: "3px",
            background: isRight ? "#333" : "#ccc"
          }}
        />
      </div>
    );
  }

  function KeyHint({ label }) {
    return (
      <span
        style={{
          padding: "0 0.3rem",
          border: "1px solid #aaa",
          borderRadius: 4,
          fontSize: "0.6rem",
          background: "#f3f3f3"
        }}
      >
        {label}
      </span>
    );
  }
  function ShortcutList() {
    return (
      <div
        style={{
          paddingTop: "0.4rem",
          fontSize: "0.65rem",
          lineHeight: 1.4
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: "0.25rem" }}>
          Gyorsbillentyűk
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr auto",
            gap: "0.25rem 0.5rem"
          }}
        >
          <span>Mérés mód</span>
          <KeyHint label="M" />
          <span>Mozgatás / Forgatás</span>
          <KeyHint label="F" />
          <span>Tapadás</span>
          <KeyHint label="R" />
          <span>Visszarendezés</span>
          <KeyHint label="T" />
          <span>Csoportosítás</span>
          <KeyHint label="G" />
          <span>Info panel</span>
          <KeyHint label="I" />
          <span>Kilépés</span>
          <KeyHint label="Esc" />
        </div>
      </div>
    );
  }
  function MoveInfo() {
    return (
      <div
        style={{
          marginTop: "0.6rem",
          paddingTop: "0.4rem",
          borderTop: "1px dashed #ddd",
          fontSize: "0.65rem",
          lineHeight: 1.4
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: "0.25rem" }}>Mozgatás</div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr auto",
            gap: "0.2rem 0.5rem"
          }}
        >
          <span>Vízszintes</span>
          <div style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}>
            <MouseIcon button="left" />
            <DragHint axis="XZ" />
          </div>

          <span>Függőleges</span>
          <div style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}>
            <MouseIcon button="right" />
            <DragHint axis="Y" />
          </div>
        </div>
      </div>
    );
  }
  function RotateInfo() {
    return (
      <div
        style={{
          marginTop: "0.6rem",
          paddingTop: "0.4rem",
          borderTop: "1px dashed #ddd",
          fontSize: "0.65rem",
          lineHeight: 1.4
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: "0.25rem" }}>Forgatás</div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr auto",
            gap: "0.2rem 0.5rem"
          }}
        >
          <span>Vízszintes</span>
          <div style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}>
            <MouseIcon button="left" />
            <DragHint axis="Y" />
          </div>

          <span>Buktatás</span>
          <div style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}>
            <MouseIcon button="right" />
            <DragHint axis="X" />
          </div>

          <span>Z tengely</span>
          <div style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}>
            <KeyHint label="ALT" />
            <MouseIcon button="left" />
          </div>
        </div>
      </div>
    );
  }

  useEffect(() => {
    const onKeyDown = (e) => {
      const tag = document.activeElement?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      if (dragStateRef.current.active) return;

      switch (e.key.toLowerCase()) {
        case "g":
          setGroupingMode((p) => !p);
          break;
        case "i": {
          setShowInfoPanel((p) => !p);
          break;
        }
        case "r": {
          const scene = sceneRef.current;
          const baseline = initialItemTransformsRef.current;
          if (!scene || !baseline) break;

          // 1️⃣ baseline lookup
          const baselineByUid = new Map(baseline.map((b) => [b.uid, b]));

          // 2️⃣ csoportonként dolgozunk
          const handledGroups = new Set();

          scene.meshes.forEach((mesh) => {
            if (!mesh.name.startsWith("box_")) return;
            const { uid, instIdx, groupId } = mesh.metadata || {};
            if (!uid || instIdx == null) return;

            const groupKey = extractGroupKey(groupId);
            if (groupKey && handledGroups.has(groupKey)) return;

            const base = baselineByUid.get(uid);
            if (!base) return;

            const posList = parseBracketList(base.position);
            const rotList = parseBracketList(base.rotation);

            const maxDim = Math.max(
              ...createdItems.map((it) => parseSizeString(it.size)).flat(),
              1
            );
            const globalScale = 1 / maxDim;

            const groupMeshes = groupKey
              ? scene.meshes.filter(
                  (m) =>
                    m.name.startsWith("box_") &&
                    extractGroupKey(m.metadata?.groupId) === groupKey
                )
              : [mesh];

            const refMesh =
              groupMeshes.find((m) => m.metadata?.instIdx === instIdx) ?? mesh;

            const [bx, by, bz] = posList[instIdx] ?? [0, 0, 0];

            const item = createdItems.find((it) => it.uid === uid);
            if (!item) return;
            const [rw, rh, rd] = parseSizeString(item.size);

            const baseWorld = new Vector3(
              bx * globalScale + (rw * globalScale) / 2,
              by * globalScale + (rh * globalScale) / 2,
              bz * globalScale + (rd * globalScale) / 2
            );

            // 5️⃣ relatív offsetek megőrzése
            groupMeshes.forEach((m) => {
              const offset = m.position.subtract(refMesh.position);
              m.position.copyFrom(baseWorld.add(offset));
            });

            handledGroups.add(groupKey);
          });

          // 6️⃣ state sync változatlan
          if (typeof onItemUpdate === "function") {
            onItemUpdate({
              type: "batch",
              updates: baseline.map((b) => ({
                uid: b.uid,
                position: b.position,
                rotation: b.rotation
              }))
            });
          }

          break;
        }

        case "m":
          clearMeasurement(sceneRef.current);
          firstPickRef.current = null;
          setMeasureMode((p) => !p);
          break;

        case "f":
          setTransformMode((p) => (p === "move" ? "rotate" : "move"));
          break;

        case "t":
          setIsSnappingEnabled((p) => !p);
          break;

        case "escape":
          clearMeasurement(sceneRef.current);
          firstPickRef.current = null;
          setMeasureMode(false);
          setIsModalOpen(false);
          selectedMeshRef.current = null;
          break;

        default:
          break;
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  useEffect(() => {
    transformModeRef.current = transformMode;
  }, [transformMode]);

  useEffect(() => {
    const cam = cameraRef.current;
    const canvas = canvasRef.current;
    if (!cam || !canvas) return;

    if (controlMode === "object") {
      cam.detachControl(canvas);
    } else {
      cam.attachControl(canvas, true);
    }
  }, [controlMode]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const prevent = (e) => e.preventDefault();
    canvas.addEventListener("contextmenu", prevent);
    return () => canvas.removeEventListener("contextmenu", prevent);
  }, []);

  const baseRadiusRef = useRef(null);
  const baseEdgeWidthRef = useRef(0.6); // a mostani defaultod
  const capsLockOnRef = useRef(false);
  const updateEdgeWidths = (scene, camera) => {
    if (!baseRadiusRef.current) baseRadiusRef.current = camera.radius;
    const r0 = baseRadiusRef.current; // kiinduló (fit utáni) radius
    const scale = camera.radius / r0; // < 1 ha közelebb vagyunk
    const clamp = (x, a, b) => Math.max(a, Math.min(b, x));

    scene.meshes.forEach((m) => {
      if (!m.edgesRenderer) return;
      const baseW = m.metadata?.edgeBaseW ?? baseEdgeWidthRef.current;
      m.edgesWidth = clamp(baseW * scale, 0.05, 2); // ízlés szerinti tartomány
    });
  };
  useEffect(() => {
    const onKey = (e) => {
      capsLockOnRef.current = e.getModifierState("CapsLock");
    };

    window.addEventListener("keydown", onKey);
    window.addEventListener("keyup", onKey);

    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("keyup", onKey);
    };
  }, []);
  // Fetch data
  useEffect(() => {
    if (objectProp && createdItems) {
      setObjectData(objectProp);
      return;
    }
    (async () => {
      const all = await dispatch(getAllObjects());
      const id = objectProp?.objectId;
      setObjectData(all.find((o) => o.objectId === id));
    })();
  }, [dispatch, objectProp, createdItems]);
  const savedCamRef = useRef(null);
  const engineRef = useRef(null);
  // Modal drag
  const [modalPos, setModalPos] = useState({
    x: window.innerWidth / 2 - 200,
    y: window.innerHeight / 2 - 150
  });
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef({ startX: 0, startY: 0, origX: 0, origY: 0 });
  const onHeaderMouseDown = (e) => {
    e.preventDefault();
    setDragging(true);
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      origX: modalPos.x,
      origY: modalPos.y
    };
  };
  useEffect(() => {
    const onMouseMove = (e) => {
      if (!dragging) return;
      const dx = e.clientX - dragRef.current.startX;
      const dy = e.clientY - dragRef.current.startY;
      setModalPos({
        x: dragRef.current.origX + dx,
        y: dragRef.current.origY + dy
      });
    };
    const onMouseUp = () => setDragging(false);
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [dragging]);

  const clearSelection = (scene) => {
    scene.meshes.forEach((m) => {
      if (!m.name.startsWith("box_")) return;
      if (!m.material) return;

      // vissza alap állapotra
      m.edgesColor = new Color4(0, 0, 0, 1);
      m.material.emissiveColor = Color3.Black();
    });
  };

  const applySelection = (mesh) => {
    if (!mesh?.material) return;

    // erős kijelölő szín
    mesh.edgesColor = new Color4(0.2, 0.9, 0.2, 1);
    mesh.material.emissiveColor = new Color3(0.1, 0.3, 0.1);
  };

  // Babylon init
  useEffect(() => {
    if (!canvasRef.current) return;
    // const engine = new Engine(canvasRef.current, true);
    const engine = new Engine(canvasRef.current, true, {
      adaptToDeviceRatio: true,
      // preserveDrawingBuffer: true,
      stencil: true
    });
    engineRef.current = engine; // ← MENTS EL
    const scene = new Scene(engine);

    scene.autoClear = false;
    scene.clearColor = new Color4(0.9, 0.9, 0.9, 1);
    sceneRef.current = scene;
    scene.onPointerDown = (evt, pickInfo) => {
      if (!pickInfo.hit) {
        clearSelection(scene);
        selectedMeshRef.current = null;
      }
    };

    scene.onPointerObservable.add((pointerInfo) => {
      const evt = pointerInfo.event;
      if (!evt) return;
      if (
        pointerInfo.type === BABYLON.PointerEventTypes.POINTERDOWN &&
        measureModeRef.current &&
        evt.shiftKey
      ) {
        const pick = scene.pick(
          scene.pointerX,
          scene.pointerY,
          (m) => m?.name?.startsWith("box_") || m?.name?.startsWith("room_"),
          false,
          cameraRef.current
        );
        if (!pick?.hit || !pick.pickedPoint) return;
        const p = pick.pickedPoint.clone();
        const normal = pick.getNormal(true)?.clone();
        const mmPos = {
          x: p.x * 1000,
          y: p.y * 1000,
          z: p.z * 1000
        };
        if (!firstPickRef.current) {
          clearMeasurement(scene);
          const mat = createMeasureMaterial(scene);
          const ring = MeshBuilder.CreateTorus(
            "measure_p1",
            { diameter: 0.045, thickness: 0.003, tessellation: 48 },
            scene
          );
          ring.position = p;
          ring.material = mat;
          ring.isPickable = false;
          orientRingToNormal(ring, normal);
          measureVisualsRef.current.p1 = ring;
          measureVisualsRef.current.p1dot = createMeasureInnerCircle(
            scene,
            p,
            normal,
            mat
          );
          measureVisualsRef.current.p1center = createMeasureCenterDot(
            scene,
            p,
            normal
          );
          firstPickRef.current = { world: p, pos: mmPos };
          return;
        }
        // 2. pont
        const { world: w0, pos: p0 } = firstPickRef.current;
        const w1 = p;
        const mat = createMeasureMaterial(scene);
        const ring2 = MeshBuilder.CreateTorus(
          "measure_p2",
          { diameter: 0.045, thickness: 0.003, tessellation: 48 },
          scene
        );
        ring2.position = w1;
        ring2.material = mat;
        ring2.isPickable = false;
        orientRingToNormal(ring2, normal);
        measureVisualsRef.current.p2 = ring2;
        measureVisualsRef.current.p2dot = createMeasureInnerCircle(
          scene,
          w1,
          normal,
          mat
        );
        measureVisualsRef.current.p2center = createMeasureCenterDot(
          scene,
          w1,
          normal
        );
        const line = MeshBuilder.CreateLines(
          "measure_line",
          { points: [w0, w1] },
          scene
        );
        line.color = new Color3(0, 1, 0);
        measureVisualsRef.current.line = line;
        const dx = Math.abs(mmPos.x - p0.x).toFixed(1);
        const dy = Math.abs(mmPos.y - p0.y).toFixed(1);
        const dz = Math.abs(mmPos.z - p0.z).toFixed(1);
        const dist = Vector3.Distance(w0, w1) * 1000;
        const mid = w0.add(w1).scale(0.5);
        const lines = [
          `X: ${dx} mm`,
          `Y: ${dy} mm`,
          `Z: ${dz} mm`,
          `Távolság: ${dist.toFixed(1)} mm`
        ];
        const fontSize = 36;
        const lineHeight = fontSize * 1.4;
        const pad = 20;
        const measureCanvas = document.createElement("canvas");
        const measureCtx = measureCanvas.getContext("2d");
        measureCtx.font = `bold ${fontSize}px Arial`;

        const w =
          Math.max(...lines.map((l) => measureCtx.measureText(l).width)) +
          pad * 2;
        const h = lines.length * lineHeight + pad * 2;

        const dt = new BABYLON.DynamicTexture(
          "measureText",
          { width: w, height: h },
          scene,
          false
        );

        // ⬅️ EZ HIÁNYZOTT
        const ctx = dt.getContext();

        ctx.clearRect(0, 0, w, h);
        ctx.font = `bold ${fontSize}px Arial`;
        ctx.textBaseline = "top";
        ctx.lineJoin = "round";
        ctx.miterLimit = 2;

        lines.forEach((l, i) => {
          const y = pad + i * lineHeight;

          ctx.lineWidth = 6;
          ctx.strokeStyle = "black";
          ctx.strokeText(l, pad, y);

          ctx.fillStyle = "white";
          ctx.fillText(l, pad, y);
        });

        dt.update();

        const plane = MeshBuilder.CreatePlane(
          "measure_label",
          { width: w * 0.0006, height: h * 0.0006 },
          scene
        );
        plane.position = mid;
        plane.billboardMode = BABYLON.Mesh.BILLBOARDMODE_ALL;
        const textMat = new StandardMaterial("textMat", scene);
        textMat.diffuseTexture = dt;
        textMat.emissiveColor = Color3.White();
        textMat.opacityTexture = dt;
        plane.material = textMat;
        measureVisualsRef.current.label = plane;

        firstPickRef.current = null;
        setMeasureMode(false);
        return;
      }
      switch (pointerInfo.type) {
        case BABYLON.PointerEventTypes.POINTERDOWN: {
          if (evt.button !== 0 && evt.button !== 2) return;
          activeGroupMeshesRef.current = [];
          clearSelection(scene);

          const pick = scene.pick(scene.pointerX, scene.pointerY);
          if (!pick?.hit || !pick.pickedMesh?.name.startsWith("box_")) return;

          const mesh = pick.pickedMesh;
          let groupMeshes = [mesh];
          if (groupingModeRef.current) {
            const groupData = scene.meshes.map((m) => {
              const meta = m.metadata || {};
              const item = createdItems.find((it) => it.uid === meta.uid);

              return {
                mesh: m.name,
                gid: extractGroupKey(meta.groupId),
                uid: meta.uid,
                itemId: meta.itemId,
                instIdx: meta.instIdx,
                createdItem: item
              };
            });

            console.log("🟡 MOVING GROUP (WITH ITEMS)", groupData);
          }
          if (
            groupingModeRef.current &&
            mesh.metadata &&
            mesh.metadata.groupId != null
          ) {
            const gid = extractGroupKey(mesh.metadata.groupId);
            groupMeshes = scene.meshes.filter(
              (m) =>
                m.name.startsWith("box_") &&
                extractGroupKey(m.metadata?.groupId) === gid
            );
          }

          activeGroupMeshesRef.current = groupMeshes;
          // 🔹 régi kijelölés törlése
          clearSelection(scene);

          // 🔹 új kijelölés
          selectedMeshRef.current = mesh;
          activeGroupMeshesRef.current.forEach((m) => applySelection(m));

          selectedMeshRef.current = mesh;

          dragStateRef.current.active = true;
          dragStateRef.current.button = evt.button; // 0 = bal, 2 = jobb
          dragStateRef.current.lastX = evt.clientX;
          dragStateRef.current.lastY = evt.clientY;
          dragStateRef.current.startPos = mesh.position.clone();
          dragStateRef.current.startRot = mesh.rotation.clone();
          dragStateRef.current.lastX = evt.clientX;
          dragStateRef.current.lastY = evt.clientY;
          dragStateRef.current.moved = false;
          dragStateRef.current.startInstIdx = mesh.metadata?.instIdx ?? null;
          cameraRef.current.detachControl(canvasRef.current);
          evt.preventDefault(); // ⬅️ FONTOS jobb gombnál

          break;
        }

        case BABYLON.PointerEventTypes.POINTERUP: {
          if (!dragStateRef.current.active) return;

          const mesh = selectedMeshRef.current;

          if (
            transformModeRef.current === "rotate" &&
            mesh &&
            !dragStateRef.current.moved
          ) {
            const snapDeg = (r) =>
              BABYLON.Tools.ToRadians(
                (Math.round(BABYLON.Tools.ToDegrees(r) / 90) * 90 + 90) % 360
              );

            if (
              dragStateRef.current.altKey &&
              dragStateRef.current.button === 0
            ) {
              mesh.rotation.z = snapDeg(mesh.rotation.z);
            } else if (dragStateRef.current.button === 0) {
              mesh.rotation.y = snapDeg(mesh.rotation.y);
            } else if (dragStateRef.current.button === 2) {
              mesh.rotation.x = snapDeg(mesh.rotation.x);
            }
          }
          // 🔴 KRITIKUS: STATE SZINKRON
          if (sceneRef.current && typeof onItemUpdate === "function") {
            const updates = collectAllMeshPositionUpdates(
              sceneRef.current
            ).filter((u) => {
              const item = createdItems.find((it) => it.uid === u.uid);
              return !item?.__userResized;
            });
            if (updates.length) {
              onItemUpdate({
                type: "batch",
                updates
              });
            }
          }
          dragStateRef.current.active = false;
          cameraRef.current.attachControl(canvasRef.current, true);
          break;
        }

        case BABYLON.PointerEventTypes.POINTERMOVE: {
          if (!dragStateRef.current.active) return;

          const dx = evt.clientX - dragStateRef.current.lastX;
          const dy = evt.clientY - dragStateRef.current.lastY;

          dragStateRef.current.lastX = evt.clientX;
          dragStateRef.current.lastY = evt.clientY;

          dragStateRef.current.altKey = evt.altKey;

          if (Math.hypot(dx, dy) > 3) {
            dragStateRef.current.moved = true;
          }
          const cam = cameraRef.current;
          const mesh = selectedMeshRef.current;
          const meshes = activeGroupMeshesRef.current.length
            ? activeGroupMeshesRef.current
            : [selectedMeshRef.current];
          if (groupingModeRef.current) {
            const groupData = meshes.map((m) => {
              const meta = m.metadata || {};
              const item = createdItems.find((it) => it.uid === meta.uid);

              if (!item) return null;

              const groupList = item._groupIdList || [];
              const groupAtIdx = groupList[meta.instIdx] || null;
              const meshGroup = extractGroupKey(meta.groupId);

              const matches = groupAtIdx?.includes(meshGroup);

              return {
                mesh: m.name,
                gid: meshGroup,
                uid: meta.uid,
                instIdx: meta.instIdx,
                groupMatch: matches,
                createdItem: matches ? item : null
              };
            });

            console.log("🟡 MOVING GROUP (WITH ITEMS)", groupData);
          }
          if (!mesh) return;

          const sensitivity = getInteractionSensitivity();

          const speed = cam.radius * 0.001 * sensitivity.move;
          const ROT_SPEED = 0.002 * sensitivity.rotate;

          // ─────────────────────────
          // MOVE MODE
          // ─────────────────────────
          if (transformModeRef.current === "move") {
            const right = cam.getDirection(Vector3.Right());
            const forward = cam.getDirection(Vector3.Forward());

            if (dragStateRef.current.button === 0) {
              // XZ
              const move = right
                .scale(dx * speed)
                .add(forward.scale(-dy * speed));
              meshes.forEach((m) => {
                m.position.x += move.x;
                m.position.z += move.z;
                m.computeWorldMatrix(true);
              });
              const item = createdItems.find(
                (it) => it.uid === mesh.metadata?.uid
              );
              if (item) item.__userResized = false;
              mesh.computeWorldMatrix(true);

              if (snappingEnabledRef.current) {
                const { dx, dz } = snapXZ(sceneRef.current, mesh);
                if (dx || dz) {
                  meshes.forEach((m) => {
                    m.position.x += dx;
                    m.position.z += dz;
                    m.computeWorldMatrix(true);
                  });
                }
              }
              if (hasCollision(sceneRef.current, mesh)) {
                //   applyCollisionVisual(mesh);
              } else {
                //  clearCollisionVisual(mesh);
              }
            }

            if (dragStateRef.current.button === 2) {
              meshes.forEach((m) => {
                m.position.y += -dy * speed;
                m.computeWorldMatrix(true);
              });
              mesh.computeWorldMatrix(true);

              if (snappingEnabledRef.current) {
                const { dy } = snapY(sceneRef.current, mesh);
                if (dy) {
                  meshes.forEach((m) => {
                    m.position.y += dy;
                    m.computeWorldMatrix(true);
                  });
                }
              }
              if (hasCollision(sceneRef.current, mesh)) {
                //   applyCollisionVisual(mesh);
              } else {
                //  clearCollisionVisual(mesh);
              }
            }
          }

          if (transformModeRef.current === "rotate") {
            // Z tengely – ALT + bal egér
            if (
              dragStateRef.current.altKey &&
              dragStateRef.current.button === 0
            ) {
              mesh.rotation.z += dx * ROT_SPEED;
              return;
            }

            // Y tengely – bal
            if (dragStateRef.current.button === 0) {
              mesh.rotation.y += dx * ROT_SPEED;
            }

            // X tengely – jobb
            if (dragStateRef.current.button === 2) {
              mesh.rotation.x += dy * ROT_SPEED;
            }
          }

          break;
        }

        default:
          break;
      }
    });

    const camera = new ArcRotateCamera(
      "cam",
      Math.PI / 2,
      Math.PI / 4,
      5000,
      Vector3.Zero(),
      scene
    );
    camera.attachControl(canvasRef.current, true);
    camera.minZ = 0.01;
    cameraRef.current = camera;
    const isTabletViewport =
      typeof window !== "undefined" &&
      window.matchMedia("(max-width: 1300px)").matches;

    camera.wheelDeltaPercentage = isTabletViewport ? 0.002 : 0.005;
    camera.pinchDeltaPercentage = isTabletViewport ? 0.002 : 0;
    const hemi = new HemisphericLight("hemi", new Vector3(0, 1, 0), scene);
    hemi.intensity = 1.0; // alap fény erősítése
    hemi.diffuse = new Color3(1, 1, 1); // közvetlen fény fehér
    hemi.groundColor = new Color3(0.8, 0.8, 0.8); // visszavert fény világosabb

    // általános ambient fény:
    scene.ambientColor = new Color3(1, 1, 1);
    engine.runRenderLoop(() => {
      engine.clear(scene.clearColor, true, true, true); // <-- mi törlünk
      scene.render();
    });
    window.addEventListener("resize", () => engine.resize());
    camera.onViewMatrixChangedObservable.add(() => {
      savedCamRef.current = {
        target: camera.target.clone(),
        alpha: camera.alpha,
        beta: camera.beta,
        radius: camera.radius
      };
      updateEdgeWidths(scene, camera);
    });
    return () => engine.dispose();
  }, []);

  const didInitialFit = useRef(false);
  // Build meshes
  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene || !objectData || createdItems.length === 0) return;
    if (!initialItemTransformsRef.current) {
      initialItemTransformsRef.current = createdItems.map((it) => ({
        uid: it.uid,
        position: it.position,
        rotation: it.rotation
      }));
    }
    // Remove old boxes and group nodes
    scene.meshes
      .filter((m) => m.name.startsWith("box_") || m.name.startsWith("grp_"))
      .forEach((m) => m.dispose(false, true));
    const maxDim = Math.max(
      ...createdItems.map((it) => parseSizeString(it.size)).flat(),
      1
    );
    const globalScale = 1 / maxDim;

    createdItems.forEach((item, groupIdx) => {
      //  const refId = item.refScriptId;
      const visKey =
        item?.itemId ?? item?.createdItemId ?? item?.tmpId ?? `tmp-${groupIdx}`;

      if (hiddenItems?.has(visKey)) {
        return; // ne építs mesh-t ehhez az elemhez
      }

      const rawPosList = parseBracketList(item.position, item.qty);
      const rawRotList = parseBracketList(item.rotation, item.qty);
      const rawGroupList = item.groupId ? extractRawList(item.groupId) : [];
      const [rw, rh, rd] = parseSizeString(item.size);
      const w = rw * globalScale,
        h = rh * globalScale,
        d = rd * globalScale;

      rawPosList.forEach(([x0, y0, z0], instIdx) => {
        const mesh = MeshBuilder.CreateBox(
          `box_${groupIdx}_${instIdx}`,
          { width: w, height: h, depth: d },
          scene
        );

        // ▸ Edge (outline) bekapcsolása
        mesh.enableEdgesRendering();
        mesh.edgesWidth = 0.6; // próbálj ki 1-4-et, ízlés szerint
        mesh.edgesColor = new Color4(0, 0, 0, 1); // fekete körvonal

        mesh.setPivotPoint(new Vector3(-w / 2, -h / 2, -d / 2));
        mesh.position = new Vector3(
          x0 * globalScale + w / 2,
          y0 * globalScale + h / 2,
          z0 * globalScale + d / 2
        );

        const [rx, ry, rz] = rawRotList[instIdx] || [0, 0, 0];
        mesh.rotation = new Vector3(
          (rx * Math.PI) / 2,
          (ry * Math.PI) / 2,
          (rz * Math.PI) / 2
        );

        const mat = new StandardMaterial(`mat_${groupIdx}_${instIdx}`, scene);
        if (item.color?.colorId != null) {
          const col = colors.find((c) => c.colorId === item.color?.colorId);
          if (col && (col.imageData || col.imageDataReduced)) {
            const img = col.imageData || col.imageDataReduced;
            // self‑lit textúra, nem árnyékolódik:
            mat.emissiveTexture = new Texture(
              `data:image/jpeg;base64,${img}`,
              scene
            );
            mat.emissiveColor = new Color3(0, 0, 0);
            mat.disableLighting = true;
          } else {
            mat.diffuseColor = new Color3(0.7, 0.7, 0.7);
          }
        } else {
          mat.diffuseColor = new Color3(0.7, 0.7, 0.7);
        }

        mesh.material = mat;

        mesh.metadata = {
          uid: item.uid,
          itemId: item.itemId,
          instIdx,
          groupId: rawGroupList[instIdx] ?? null
        };

        mesh.actionManager = new ActionManager(scene);
        millingDisplay.attach(mesh, item, { w, h, d, widthMm: rh, heightMm: rw, swapAxes: true });
      });
    });

    // Recenter camera
    const cam = cameraRef.current;
    //    fitCamera(scene, cam);
    if (!didInitialFit.current) {
      fitCameraToMeshes(scene, cam);
      didInitialFit.current = true;
    }
  }, [objectData, createdItems, colors, dispatch, hiddenItems]);

  const fitCameraToMeshes = (scene, camera) => {
    // csak azok a mesh-ek, amik a dobozaid:
    const boxes = scene.meshes.filter((m) => m.name.startsWith("box_"));
    if (!boxes.length) return;

    // egyesítsd mindnek a bounding box-át
    let min = boxes[0].getBoundingInfo().boundingBox.minimumWorld.clone();
    let max = boxes[0].getBoundingInfo().boundingBox.maximumWorld.clone();
    for (let i = 1; i < boxes.length; i++) {
      const bi = boxes[i].getBoundingInfo().boundingBox;
      min = Vector3.Minimize(min, bi.minimumWorld);
      max = Vector3.Maximize(max, bi.maximumWorld);
    }

    // középpont és sugar
    const center = min.add(max).scale(150);
    const radius = max.subtract(min).length();

    // beállítod a kamerát
    camera.setTarget(center);
    // fov figyelembevételével kiszámolod, hogy mekkora távolság kell:
    const distance = radius / Math.sin(camera.fov / 2);
    camera.radius = distance * 1.1; // kis ráhagyás
    camera.lowerRadiusLimit = distance * 0.2;
    camera.upperRadiusLimit = distance * 10;
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    //  const engine = sceneRef.current.getEngine();

    const engine = sceneRef.current.getEngine();
    const scheduled = { current: false };

    const ro = new ResizeObserver(() => {
      if (scheduled.current) return;
      scheduled.current = true;

      requestAnimationFrame(() => {
        scheduled.current = false;
        engine.resize(); // nem lőjjük el a loop-ot
      });
    });

    ro.observe(canvas);
    return () => ro.disconnect();
  }, []);

  return (
    <>
      <div
        ref={infoPanelRef}
        style={{
          position: "absolute",
          top: 10,
          right: 10,
          zIndex: 20
        }}
      >
        {!showInfoPanel && (
          <div
            onClick={() => setShowInfoPanel(true)}
            style={{
              width: 32,
              height: 32,
              borderRadius: "50%",
              background: "#fff",
              border: "1px solid #ccc",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              boxShadow: "0 2px 6px rgba(0,0,0,0.15)"
            }}
          >
            ⓘ
          </div>
        )}

        {showInfoPanel && (
          <div
            style={{
              width: 200,
              borderRadius: 18,
              background: "rgba(255,255,255,0.95)",
              border: "1px solid #ccc",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              overflow: "hidden"
            }}
          >
            {/* header */}
            <div
              style={{
                padding: "0.6rem 0.75rem",
                borderBottom: "1px dashed #ddd",
                position: "relative"
              }}
            >
              <strong>Információk</strong>
              <div
                onClick={() => setShowInfoPanel(false)}
                style={{
                  position: "absolute",
                  top: 3,
                  right: 3,
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  border: "1px solid #ccc",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer"
                }}
              >
                ⓘ
              </div>
            </div>

            {/* content */}
            <div
              style={{
                padding: "0.6rem 0.75rem",
                paddingTop: "0",
                fontSize: "0.7rem",
                textAlign: "left"
              }}
            >
              <ShortcutList />

              {transformMode === "move" && <MoveInfo />}

              {transformMode === "rotate" && <RotateInfo />}

              {groupingMode && (
                <div style={{ marginTop: 8 }}>🧩 Csoportos mód aktív</div>
              )}
            </div>
          </div>
        )}
      </div>

      <div
        ref={containerRef}
        style={{ position: "relative", width: "100%", height: "100%" }}
      >
        <FloatingToolbar
          showMilling3D={millingDisplay.show3D}
          onToggleMilling3D={millingDisplay.toggle}
          millingStatus={millingDisplay.status}
          onRetryMilling={millingDisplay.retry}
          containerRef={containerRef}
          measureMode={measureMode}
          onToggleMeasure={() => {
            // ObjectViewer-ben még nincs mérés → noop
            setMeasureMode((p) => !p);
          }}
          snappingEnabled={isSnappingEnabled}
          onToggleSnapping={() => setIsSnappingEnabled((p) => !p)}
          controlMode={controlMode}
          onToggleControlMode={() =>
            setControlMode((p) => (p === "camera" ? "object" : "camera"))
          }
          transformMode={transformMode}
          onToggleTransformMode={() =>
            setTransformMode((p) => (p === "move" ? "rotate" : "move"))
          }
          groupingEnabled={groupingMode}
          onToggleGrouping={() => setGroupingMode((p) => !p)}
        />
        <canvas
          ref={canvasRef}
          style={{
            width: "100%",
            height: "100%",

            display: "block",
            backgroundColor: "#FFFFFF" // itt is megadhatod
          }}
        />
        <Modal
          show={isModalOpen}
          onHide={() => setIsModalOpen(false)}
          backdropClassName="no-blur-backdrop"
          centered={false}
          dialogAs={({ className, style, children, ...props }) => (
            <div
              {...props}
              className={className}
              style={{
                ...style,
                position: "absolute",
                width: "15vw",
                top: modalPos.y,
                left: modalPos.x,
                margin: 0,
                backgroundColor: "#fff",
                borderRadius: "0.3rem",
                boxShadow: "0 2px 10px rgba(0,0,0,0.2)"
              }}
            >
              {children}
            </div>
          )}
        >
          <Modal.Header
            //  closeButton
            onMouseDown={onHeaderMouseDown}
            style={{ cursor: "move" }}
          >
            <Modal.Title style={{ flex: 1, margin: 0 }}></Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {["x", "y", "z"].map((ax) => (
              <div key={ax} style={{ marginBottom: 12 }}>
                <input
                  type="range"
                  max={2500}
                  onChange={(e) => {}}
                  style={{ width: "100%" }}
                />
              </div>
            ))}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setIsModalOpen(false)}>
              Cancel
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </>
  );
}

// Helpers
function parseSizeString(input) {
  if (Array.isArray(input)) {
    const nums = input.map((n) => parseFloat(n) || 0);
    return nums.length === 3 ? nums : [1, 1, 1];
  }
  if (typeof input != "string") return [1, 1, 1];
  return input
    .slice(1, -1)
    .split(",")
    .map((n) => parseFloat(n) || 0);
}
function parseBracketList(input = "", qty) {
  if (Array.isArray(input))
    return input.slice(0, qty).map((a) => {
      if (!Array.isArray(a)) return [0, 0, 0];
      return a.slice(0, 3).map((n) => parseFloat(n) || 0);
    });
  const matches = (input.match(/\[.*?\]/g) || []).slice(0, qty);
  return Array.from({ length: qty }, (_, i) => {
    const m = matches[i];
    if (!m) return [0, 0, 0];
    const parts = m.slice(1, -1).split(",");
    return parts.slice(0, 3).map((n) => parseFloat(n) || 0);
  });
}
function extractRawList(str) {
  if (!str) return [];
  const s = String(str).trim();

  if (s.startsWith("[[") && s.endsWith("]]")) {
    const inner = s.slice(1, -1);
    const raws = splitTopLevel(inner);
    return raws.map((r) => r.replace(/^\[|\]$/g, ""));
  }

  // ha van ?, passzoljuk tovább egyben
  if (s.includes("?")) return [s];

  // egyébként csak a "[…]" blokkokat gyűjtsük ki
  const matches = s.match(/\[[^\]]*\]/g) || [];
  return matches.length > 0 ? matches : [s];
}
function splitTopLevel(str) {
  const parts = [];
  let depthPar = 0,
    depthBr = 0,
    last = 0;
  for (let i = 0; i < str.length; i++) {
    const ch = str[i];
    if (ch === "(") depthPar++;
    else if (ch === ")") depthPar--;
    else if (ch === "[") depthBr++;
    else if (ch === "]") depthBr--;
    else if (ch === "," && depthPar === 0 && depthBr === 0) {
      parts.push(str.slice(last, i).trim());
      last = i + 1;
    }
  }
  parts.push(str.slice(last).trim());
  return parts;
}
function extractGroupKey(groupId) {
  if (!groupId) return null;

  // "[x,y,z,INST:61|1]" → "INST:61|1"
  const m = String(groupId).match(/,(INST:[^\]]+|REF:[^\]]+)\]?$/);
  return m ? m[1] : groupId;
}
